import type {
  MemberCategory,
  MemberCategoryAssignment,
  PublicMemberCategory,
} from '../../../services/memberCategoriesService'

/**
 * Pure helpers for the Member Taxonomy UI (self-select / staff manage / public display).
 * Kept framework-free so the confirmed/proposed/primary logic is unit-tested (see __tests__).
 * Assignment shape (backend): { category: <id>, categoryName: <string>, isPrimary, status }.
 */

export const STATUS_PROPOSED = 'member_proposed'
export const STATUS_CONFIRMED = 'staff_confirmed'

export const isConfirmed = (a: MemberCategoryAssignment): boolean => a.status === STATUS_CONFIRMED

export const isProposed = (a: MemberCategoryAssignment): boolean => a.status === STATUS_PROPOSED

/** Only staff-confirmed assignments are public (brief T2). */
export const confirmedAssignments = (
  list: MemberCategoryAssignment[]
): MemberCategoryAssignment[] => (list ?? []).filter(isConfirmed)

export const proposedAssignments = (list: MemberCategoryAssignment[]): MemberCategoryAssignment[] =>
  (list ?? []).filter(isProposed)

/** The member's primary (qualifying) assignment, if any. */
export const findPrimary = (list: MemberCategoryAssignment[]): MemberCategoryAssignment | null =>
  (list ?? []).find((a) => a.isPrimary) ?? null

export const isCategorySelected = (list: MemberCategoryAssignment[], categoryId: number): boolean =>
  (list ?? []).some((a) => a.category === categoryId)

export const assignmentForCategory = (
  list: MemberCategoryAssignment[],
  categoryId: number
): MemberCategoryAssignment | null => (list ?? []).find((a) => a.category === categoryId) ?? null

/**
 * Display order for chips/labels: primary first, then remaining confirmed alphabetically. Used for
 * the public profile render and the members-screen chip source (confirmed-only).
 */
export const orderedConfirmedNames = (list: MemberCategoryAssignment[]): string[] =>
  confirmedAssignments(list)
    .slice()
    .sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1
      return a.categoryName.localeCompare(b.categoryName)
    })
    .map((a) => a.categoryName)

/** Active catalog categories only (soft-deleted ones are hidden from pickers/chips). */
export const activeCategories = (list: MemberCategory[]): MemberCategory[] =>
  (list ?? []).filter((c) => c.isActive !== false)

/** Public categories (already confirmed by the backend) ordered primary-first then alphabetical (T5). */
export const orderedPublicCategories = (list: PublicMemberCategory[]): PublicMemberCategory[] =>
  (list ?? []).slice().sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1
    return a.name.localeCompare(b.name)
  })

/** Catalog categories not yet assigned to the member (for the staff "assign" picker, WP-T4). */
export const unassignedCategories = (
  catalog: MemberCategory[],
  assignments: MemberCategoryAssignment[]
): MemberCategory[] => {
  const taken = new Set((assignments ?? []).map((a) => a.category))
  return activeCategories(catalog).filter((c) => !taken.has(c.id))
}
